const KEY_ID = "oc-mct-frontend";
const ALGORITHM = "hmac-sha256";
// Note: "date" is a forbidden browser request header; "host" is automatically
// sent by the browser. We use "x-date" (settable) and compute "host" from the
// URL. Kong accepts X-Date as an alternative to Date for clock-skew validation.
const SIGNED_HEADERS = "x-date @request-target host";

/**
 * Builds HMAC-SHA256 Authorization and X-Date headers for a request.
 *
 * Returns null when no secret has been configured (e.g. local dev, prod before
 * the credential is provisioned). Kong falls back to the anonymous consumer in
 * that case, matching the desired degraded-gracefully behavior.
 *
 * The "host" header is included in the signing string but NOT set on the
 * returned object — the browser sends it automatically and Kong reads it from
 * the real request when verifying. "X-Date" IS set because it is not a
 * forbidden browser header.
 */
export async function buildHmacHeaders(
  method: string,
  url: string,
  secret: string | null = null
): Promise<{ xDate: string; authorization: string } | null> {
  if (!secret) return null;

  // Resolve against the current page so protocol-relative and relative URLs
  // become fully qualified (needed to extract host and pathname).
  const resolved = new URL(url, window.location.href);
  const host = resolved.host;
  const requestTarget = `${method.toLowerCase()} ${resolved.pathname}${resolved.search}`;
  const xDate = new Date().toUTCString();

  const signingString = [
    `x-date: ${xDate}`,
    `@request-target: ${requestTarget}`,
    `host: ${host}`,
  ].join("\n");

  const encoder = new TextEncoder();
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    encoder.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );

  const signatureBuffer = await crypto.subtle.sign(
    "HMAC",
    cryptoKey,
    encoder.encode(signingString)
  );

  // HMAC-SHA256 output is always 32 bytes — safe to spread into fromCodePoint.
  const signature = btoa(
    String.fromCodePoint(...new Uint8Array(signatureBuffer))
  );

  const authorization = `hmac username="${KEY_ID}", algorithm="${ALGORITHM}", headers="${SIGNED_HEADERS}", signature="${signature}"`;

  return { xDate, authorization };
}
